/**
 * ============================================================================
 * MASTER PAGE: User Master Management (`tbl_user_master` - Sheet 1)
 * ============================================================================
 * Purpose:
 *   Clean, intuitive administrative interface for managing platform users,
 *   administrators, client accounts, roles, and credentials.
 *
 * Database Scope:
 *   Master Database (`digimaster`)
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../../core/services/api";
import { userService, roleService, accountService, countryService, authService } from "../../services";
import { getCurrentUser, isRegularUser, isSuperAdmin } from "../../../core/utils/auth";
import { STORAGE_KEYS } from "../../../core/config/constants";

// SVG Icons
const DevicePhoneMobileIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
  </svg>
);

const MagnifyingGlassIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const PencilSquareIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
  </svg>
);

const TrashIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const EyeIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const LockClosedIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
  </svg>
);

const UsersIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
  </svg>
);

const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

const FunnelIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const ServerStackIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.75 5.1a3 3 0 0 1 2.4-1.35h7.7a3 3 0 0 1 2.4 1.35l2.1 3.45a4.5 4.5 0 0 1 .9 2.7" />
  </svg>
);

const ClipboardDocumentIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.842A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.592m7.332 0c.055.194.084.4.084.608v1.05a2.25 2.25 0 0 1-2.25 2.25h-3a2.25 2.25 0 0 1-2.25-2.25V4.45c0-.208.03-.414.085-.608m7.332 0l-.82 11.48A2.25 2.25 0 0 1 12.83 17.25H6.17a2.25 2.25 0 0 1-2.246-1.928L3.104 3.842" />
  </svg>
);

const BoltIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
  </svg>
);

const PlayIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
  </svg>
);

const PauseIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
  </svg>
);

const StopIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 7.5A2.25 2.25 0 0 1 7.5 5.25h9a2.25 2.25 0 0 1 2.25 2.25v9a2.25 2.25 0 0 1-2.25 2.25h-9a2.25 2.25 0 0 1-2.25-2.25v-9Z" />
  </svg>
);

const isRootSuperAdmin = (user) => {
  if (!user) return false;
  const username = (user.str_username || "").toLowerCase().trim();
  const roleId = String(user.str_role_id || user.int_role_id || "");
  const roleName = (user.role?.str_role_name || user.str_role_name || "").toLowerCase();
  const name = (user.str_name || "").toLowerCase();

  return (
    username === "superadmin" ||
    roleId === "1" ||
    roleName.includes("superadmin") ||
    roleName.includes("super admin") ||
    (name.includes("super administrator") && (roleId === "1" || roleId === "2"))
  );
};

export default function UserManagement() {
  const navigate = useNavigate();
  const currentUser = useMemo(() => getCurrentUser(), []);
  const currentUsername = currentUser?.str_username || "";
  const currentUserId = currentUser?._id || "";
  const isClient = useMemo(() => isRegularUser(currentUser), [currentUser]);
  const isSuper = useMemo(() => isSuperAdmin(currentUser), [currentUser]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, totalPages: 1, total: 0 });
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all"); // "all" | "admin" | "client"
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "active" | "inactive" | "suspended"

  // Dynamic roles, client accounts & countries
  const [roles, setRoles] = useState([]);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [fetchedAccount, setFetchedAccount] = useState(null);

  // Modal States
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState(null);

  // Navigate to Dedicated Queue & Messaging Limit Page
  const handleNavigateToQueue = (user) => {
    const targetId = user._id || user.str_username;
    navigate(`/admin/users/queue/${targetId}`);
  };

  const [formData, setFormData] = useState({
    str_name: "",
    str_username: "",
    str_email: "",
    str_password: "",
    str_mobile_no: "",
    str_role_id: "15",
    str_account_id: "",
    str_status: "active",
    bool_is_active: true,
  });

  const loadMetadata = useCallback(async () => {
    try {
      const [roleRes, accRes, cntRes] = await Promise.all([
        roleService.get({ limit: 100, sortBy: "int_role_hierarchy", sortOrder: "desc" }),
        accountService.get({ limit: 100 }),
        countryService.get({ limit: 300 }),
      ]);
      if (roleRes.success) setRoles(roleRes.data || []);
      if (accRes.success) setClientAccounts(accRes.data || []);
      if (cntRes.success) setCountries(cntRes.data || []);
    } catch (err) {
      console.error("Failed to load metadata:", err);
    }
  }, []);

  const clientAccount = useMemo(() => {
    if (!currentUser?.str_account_id) return null;
    return clientAccounts.find(
      (a) =>
        String(a.int_account_id) === String(currentUser.str_account_id) ||
        String(a._id) === String(currentUser.str_account_id)
    );
  }, [currentUser, clientAccounts]);

  useEffect(() => {
    if (!isViewOpen || !selectedUser?.str_account_id) {
      setFetchedAccount(null);
      return;
    }
    const accId = String(selectedUser.str_account_id);
    const existing = clientAccounts.find(
      (a) => String(a.int_account_id) === accId || String(a._id) === accId
    );
    if (existing) {
      setFetchedAccount(existing);
    } else {
      accountService
        .view(accId)
        .then((res) => {
          if (res?.success && res.data) {
            setFetchedAccount(res.data);
          }
        })
        .catch((e) => console.warn("Could not fetch account for drawer:", e));
    }
  }, [isViewOpen, selectedUser, clientAccounts]);

  const activeAccount = fetchedAccount || clientAccounts.find(
    (a) =>
      selectedUser?.str_account_id &&
      (String(a.int_account_id) === String(selectedUser.str_account_id) ||
        String(a._id) === String(selectedUser.str_account_id))
  );

  const getCountryFlag = (code) => {
    if (!code || code.length !== 2) return "🌐";
    try {
      const codePoints = code
        .toUpperCase()
        .split("")
        .map((char) => 127397 + char.charCodeAt(0));
      return String.fromCodePoint(...codePoints);
    } catch {
      return "🌐";
    }
  };

  const getCountryName = useCallback((code) => {
    if (!code) return "—";
    const clean = code.trim().toUpperCase();
    if (clean === "IN" || clean === "IND") return "India";
    if (clean === "RU" || clean === "RUS") return "Russia";
    if (clean === "US" || clean === "USA") return "United States";
    if (clean === "AE" || clean === "ARE") return "United Arab Emirates";
    if (clean === "GB" || clean === "GBR") return "United Kingdom";
    if (clean === "SA" || clean === "SAU") return "Saudi Arabia";
    if (clean === "SG" || clean === "SGP") return "Singapore";

    const found = countries.find(
      (c) =>
        c.str_country_code?.toUpperCase() === clean ||
        c.str_country_iso?.toUpperCase() === clean
    );
    return found?.str_country_name || clean;
  }, [countries]);

  const isDomesticRoute = (code) => {
    if (!code) return false;
    const clean = code.trim().toUpperCase();
    return clean === "IN" || clean === "IND";
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userService.get({
        page: pagination.page,
        limit: pagination.limit,
        search: isClient ? currentUsername : search,
        excludeSuper: !isSuper,
      });
      if (res && res.success) {
        let fetched = res.data || [];
        if (!isSuper) {
          fetched = fetched.filter((u) => !isRootSuperAdmin(u));
        }
        if (isClient) {
          fetched = fetched.filter((u) => {
            const matchId = currentUserId && String(u._id) === String(currentUserId);
            const matchIntId = currentUser?.int_user_id && Number(u.int_user_id) === Number(currentUser.int_user_id);
            const matchUname = currentUsername && u.str_username === currentUsername;
            return matchId || matchIntId || matchUname;
          });
          if (fetched.length === 0 && currentUser) {
            fetched = [currentUser];
          }
        }
        setUsers(fetched);
        if (res.pagination) {
          setPagination((prev) => {
            let newTotal = isClient ? fetched.length : (res.pagination.total ?? prev.total);
            if (!isSuper && res.pagination?.total && fetched.length < (res.data || []).length) {
              newTotal = Math.max(0, newTotal - ((res.data || []).length - fetched.length));
            }
            const newTotalPages = isClient ? 1 : (Math.ceil(newTotal / pagination.limit) || 1);
            if (prev.total === newTotal && prev.totalPages === newTotalPages) {
              return prev;
            }
            return { ...prev, total: newTotal, totalPages: newTotalPages };
          });
        }
      } else if (isClient && currentUser) {
        setUsers([currentUser]);
      }
    } catch (err) {
      console.error("Failed to load users:", err.message);
      if (isClient && currentUser) {
        setUsers([currentUser]);
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, isClient, currentUsername, currentUserId, currentUser, isSuper]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const isSelectedRoleAdmin = formData.str_role_id === "1" || formData.str_role_id === "2";

  const handleOpenCreate = () => {
    setSelectedUser(null);
    setFormData({
      str_name: "",
      str_username: "",
      str_email: "",
      str_password: "",
      str_mobile_no: "",
      str_role_id: "15",
      str_account_id: "",
      str_status: "active",
      bool_is_active: true,
    });
    setIsFormOpen(true);
  };

  const handleOpenEdit = (user) => {
    if (isRootSuperAdmin(user) && !isSuper) {
      Swal.fire({
        title: "Permission Denied",
        text: "Only Super Administrators can edit this account.",
        icon: "error",
        confirmButtonColor: "#0f172a",
      });
      return;
    }
    setSelectedUser(user);
    setFormData({
      str_name: user.str_name || "",
      str_username: user.str_username || "",
      str_email: user.str_email || "",
      str_password: "",
      str_mobile_no: user.str_mobile_no || "",
      str_role_id: String(user.str_role_id === "4" || user.str_role_id === "7" ? "15" : (user.str_role_id || "15")),
      str_account_id: user.str_account_id || "",
      str_status: user.str_status || "active",
      bool_is_active: user.bool_is_active ?? true,
    });
    setIsFormOpen(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (selectedUser && isRootSuperAdmin(selectedUser) && !isSuper) {
      Swal.fire({
        title: "Permission Denied",
        text: "Only Super Administrators can edit this account.",
        icon: "error",
        confirmButtonColor: "#0f172a",
      });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...formData };
      if (payload.str_role_id === "1" || payload.str_role_id === "2") {
        payload.str_account_id = "";
      }

      if (selectedUser) {
        if (!payload.str_password) delete payload.str_password;
        await userService.edit(selectedUser._id, payload);
        setFeedbackMsg({
          type: "success",
          text: `User "${payload.str_name || payload.str_username}" updated successfully.`,
        });
      } else {
        await userService.create(payload);
        setFeedbackMsg({
          type: "success",
          text: `New user "${payload.str_name || payload.str_username}" created successfully.`,
        });
      }
      setIsFormOpen(false);
      loadUsers();
      setTimeout(() => setFeedbackMsg(null), 4000);
    } catch (err) {
      alert(err.message || "Failed to save user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async (user) => {
    if (isClient) {
      Swal.fire({
        title: "Permission Denied",
        text: "Client users do not have permissions to edit user accounts.",
        icon: "warning",
        confirmButtonColor: "#0f172a",
      });
      return;
    }

    if (isRootSuperAdmin(user) && !isSuper) {
      Swal.fire({
        title: "Permission Denied",
        text: "Only Super Administrators can edit the Super Administrator account.",
        icon: "error",
        confirmButtonColor: "#0f172a",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Edit User Account?",
      html: `
        <div style="font-size:13px;color:#475569;line-height:1.6;">
          Open edit wizard for <b>${user.str_name || user.str_username}</b> (@${user.str_username})?
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Edit User",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0f172a",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      navigate(`/admin/users/edit/${user._id}`);
    }
  };

  const handleDeleteUser = async (user) => {
    if (isRootSuperAdmin(user)) {
      Swal.fire({
        title: "Action Not Allowed",
        text: "The root SuperAdmin account cannot be deleted.",
        icon: "warning",
        confirmButtonColor: "#0f172a",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Delete User Account?",
      html: `
        <div style="font-size:13px;color:#475569;line-height:1.6;">
          Are you sure you want to delete <b>${user.str_name || user.str_username}</b> (@${user.str_username})?
          <p style="margin-top:8px;font-size:12px;color:#dc2626;font-weight:500;">
            This user account will be deleted.
          </p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete User",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const res = await userService.delete(user._id);
      if (res?.success === false) {
        throw new Error(res.error || "Failed to delete user.");
      }

      await Swal.fire({
        title: "User Deleted!",
        text: `User "${user.str_name || user.str_username}" has been deactivated and soft-deleted.`,
        icon: "success",
        confirmButtonColor: "#0f172a",
      });

      loadUsers();
      if (isViewOpen && selectedUser?._id === user._id) {
        setIsViewOpen(false);
        setSelectedUser(null);
      }
    } catch (err) {
      Swal.fire({
        title: "Delete Failed",
        text: err.message || "Failed to delete user account.",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handleManageEmbeddedSignup = async (user) => {
    const isCurrentlyEnabled = Boolean(user.bool_embedded_signup_enabled);
    const currentLimit = Number(
      user.int_embedded_signup_limit !== undefined && user.int_embedded_signup_limit !== null
        ? user.int_embedded_signup_limit
        : 0
    );
    const currentUsed = Number(user.int_embedded_signup_used || 0);

    const { value: formValues } = await Swal.fire({
      title: "Meta Embedded Signup Quota",
      html: `
        <div style="text-align: left; font-size: 13px; color: #475569; margin-top: 10px;">
          <p style="margin-bottom: 12px;">Configure Embedded Signup for <b>${user.str_name || user.str_username}</b> (@${user.str_username}):</p>
          
          <div style="margin-bottom: 15px; padding: 12px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0;">
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; font-weight: 600; color: #1e293b;">
              <input type="checkbox" id="swal_enable_embedded" ${isCurrentlyEnabled ? "checked" : ""} style="width: 16px; height: 16px; accent-color: #059669;" />
              Enable Embedded Signup
            </label>
            <span style="font-size: 11px; color: #64748b; margin-left: 24px; display: block; margin-top: 4px;">
              When enabled, the user will see and be able to click the "Embedded Signup" button on their Phone Numbers screen.
            </span>
          </div>

          <div style="margin-bottom: 10px;">
            <label style="display: block; font-weight: 600; color: #1e293b; margin-bottom: 4px;">Allowed Signup Limit (Times)</label>
            <input type="number" id="swal_embedded_limit" value="${currentLimit}" min="0" max="999" style="width: 100%; padding: 8px 12px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px;" />
            <span style="font-size: 11px; color: #64748b; display: block; margin-top: 4px;">Times connected so far: <b>${currentUsed}</b> (Set to 0 if not allowed)</span>
          </div>
        </div>
      `,
      didOpen: () => {
        const chk = document.getElementById("swal_enable_embedded");
        const inp = document.getElementById("swal_embedded_limit");
        if (chk && inp) {
          chk.addEventListener("change", (e) => {
            if (!e.target.checked) {
              inp.value = 0;
            } else if (parseInt(inp.value, 10) === 0) {
              inp.value = 1;
            }
          });
        }
      },
      showCancelButton: true,
      confirmButtonText: "Save Quota",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#059669",
      cancelButtonColor: "#64748b",
      preConfirm: () => {
        const enabled = document.getElementById("swal_enable_embedded").checked;
        const rawLimit = document.getElementById("swal_embedded_limit").value;
        const limit = rawLimit === "" ? 0 : Math.max(0, parseInt(rawLimit, 10) || 0);
        return { enabled, limit };
      },
    });

    if (formValues) {
      try {
        const res = await api.put("/whatsapp-embedded/update-quota", {
          userId: user._id,
          bool_embedded_signup_enabled: formValues.enabled,
          int_embedded_signup_limit: formValues.limit,
        });
        if (res && (res.success || res.data?.success)) {
          Swal.fire({
            title: "Quota Updated",
            text: `Embedded signup settings saved. Status: ${formValues.enabled ? "Enabled" : "Disabled"}, Limit: ${formValues.limit}`,
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          loadUsers();
          if (selectedUser && selectedUser._id === user._id) {
            setSelectedUser({
              ...selectedUser,
              bool_embedded_signup_enabled: formValues.enabled,
              int_embedded_signup_limit: formValues.limit,
            });
          }
        } else {
          Swal.fire({
            title: "Update Failed",
            text: res?.error || res?.data?.error || "Failed to update quota.",
            icon: "error",
          });
        }
      } catch (err) {
        Swal.fire({
          title: "Update Failed",
          text: err.response?.data?.error || err.message || "Failed to update quota.",
          icon: "error",
        });
      }
    }
  };

  const handleTogglePermission = async (user, fieldName, featureName) => {
    if (isClient) return;
    const currentVal = fieldName === "bool_chat_enabled" ? user[fieldName] !== false : Boolean(user[fieldName]);
    const nextVal = !currentVal;

    try {
      const res = await userService.edit(user._id, { [fieldName]: nextVal });
      if (res && (res.success || res.data?.success)) {
        setFeedbackMsg({
          type: "success",
          text: `${featureName} is now ${nextVal ? "Enabled" : "Disabled"} for @${user.str_username}`,
        });
        setTimeout(() => setFeedbackMsg(null), 3500);

        setUsers((prev) =>
          prev.map((u) => (u._id === user._id ? { ...u, [fieldName]: nextVal } : u))
        );
        setSelectedUser((prev) => (prev && prev._id === user._id ? { ...prev, [fieldName]: nextVal } : prev));

        // If the updated user is currently logged in on this browser, synchronize localStorage
        const cur = getCurrentUser();
        if (cur && cur._id === user._id) {
          const updatedUser = { ...cur, [fieldName]: nextVal };
          localStorage.setItem(STORAGE_KEYS?.USER || "user", JSON.stringify(updatedUser));
          window.dispatchEvent(new Event("storage"));
          window.dispatchEvent(new Event("user-permissions-changed"));
        }
      } else {
        Swal.fire({
          title: "Update Failed",
          text: res?.error || `Failed to update ${featureName}`,
          icon: "error",
          confirmButtonColor: "#0f172a",
        });
      }
    } catch (err) {
      Swal.fire({
        title: "Update Error",
        text: err.message || `Failed to update ${featureName}`,
        icon: "error",
        confirmButtonColor: "#0f172a",
      });
    }
  };

  const handleToggle2FA = async (targetUser) => {
    if (!targetUser) return;
    try {
      const targetState = !targetUser.bool_two_factor_enabled;
      const res = await authService.toggle2FA({
        userId: targetUser._id,
        username: targetUser.str_username,
        enabled: targetState,
      });

      if (res?.success) {
        setFeedbackMsg({
          type: "success",
          text: `2FA ${targetState ? "activated" : "deactivated"} for @${targetUser.str_username}.`,
        });
        setSelectedUser((prev) => (prev ? { ...prev, bool_two_factor_enabled: targetState } : prev));
        setUsers((prev) =>
          prev.map((u) => (u._id === targetUser._id ? { ...u, bool_two_factor_enabled: targetState } : u))
        );
        setTimeout(() => setFeedbackMsg(null), 3500);
      }
    } catch (err) {
      Swal.fire({
        title: "2FA Update Failed",
        text: err.response?.data?.message || err.message || "Failed to toggle 2FA.",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const getRoleInfo = useCallback((roleId) => {
    const rId = String(roleId || "");
    const found = roles.find((r) => String(r.int_role_id) === rId || String(r._id) === rId);
    let roleName = found?.str_role_name || (
      rId === "1" ? "SuperAdmin" :
      rId === "2" || rId === "3" || rId === "13" ? "Admin" :
      rId === "14" ? "Reseller" :
      rId === "5" ? "Company" :
      rId === "6" ? "Account" :
      rId === "4" || rId === "7" || rId === "15" ? "User" :
      rId === "8" ? "Agent" :
      rId ? `Role ${rId}` : "—"
    );
    const normalized = roleName.toLowerCase();

    let badgeColor = "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    if (normalized.includes("super")) {
      badgeColor = "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300";
    } else if (normalized.includes("admin")) {
      badgeColor = "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300";
    } else if (normalized.includes("reseller")) {
      badgeColor = "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300";
    } else if (normalized.includes("company") || normalized.includes("account")) {
      badgeColor = "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300";
    } else if (normalized.includes("user")) {
      badgeColor = "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300";
    } else if (normalized.includes("agent")) {
      badgeColor = "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300";
    }

    let title = roleName.charAt(0).toUpperCase() + roleName.slice(1);
    if (normalized === "superadmin") title = "Super Admin";
    const hierarchy = found ? (found.int_role_hierarchy ?? found.int_role_hirarchy) : null;

    return {
      name: title,
      hierarchy,
      badgeColor,
    };
  }, [roles]);

  const isRoleAdmin = useCallback((roleId) => {
    const rId = String(roleId || "");
    if (rId === "1" || rId === "2" || rId === "3" || rId === "13") return true;
    const found = roles.find((r) => String(r.int_role_id) === rId || String(r._id) === rId);
    if (found) {
      const h = Number(found.int_role_hierarchy ?? found.int_role_hirarchy ?? 0);
      const n = (found.str_role_name || "").toLowerCase();
      return h >= 90 || n.includes("super") || n.includes("admin");
    }
    return false;
  }, [roles]);

  const filteredUsers = useMemo(() => {
    let list = users;
    if (!isSuper) {
      list = list.filter((u) => !isRootSuperAdmin(u));
    }
    if (isClient) {
      return list.filter((u) => {
        const matchId = currentUser?._id && String(u._id) === String(currentUser._id);
        const matchIntId = currentUser?.int_user_id && Number(u.int_user_id) === Number(currentUser.int_user_id);
        const matchUname = currentUser?.str_username && u.str_username === currentUser.str_username;
        return matchId || matchIntId || matchUname;
      });
    }
    return list.filter((u) => {
      const isAdm = isRoleAdmin(u.str_role_id);
      if (filterType === "admin" && !isAdm) return false;
      if (filterType === "client" && isAdm) return false;
      if (statusFilter !== "all") {
        const uStatus = (u.str_status || "active").toLowerCase();
        if (uStatus !== statusFilter) return false;
      }
      return true;
    });
  }, [users, filterType, statusFilter, isRoleAdmin, isClient, currentUser, isSuper]);

  const accessibleUsers = useMemo(() => {
    if (!isSuper) {
      return users.filter((u) => !isRootSuperAdmin(u));
    }
    return users;
  }, [users, isSuper]);

  const totalUsers = isSuper ? (pagination.total || users.length) : (pagination.total || accessibleUsers.length);
  const adminCount = useMemo(
    () => accessibleUsers.filter((u) => isRoleAdmin(u.str_role_id) && (!isSuper ? !isRootSuperAdmin(u) : true)).length,
    [accessibleUsers, isRoleAdmin, isSuper]
  );
  const clientCount = useMemo(
    () => accessibleUsers.filter((u) => !isRoleAdmin(u.str_role_id)).length,
    [accessibleUsers, isRoleAdmin]
  );
  const activeCount = useMemo(
    () => accessibleUsers.filter((u) => (u.str_status === "active" || u.bool_is_active) && (!isSuper ? !isRootSuperAdmin(u) : true)).length,
    [accessibleUsers, isSuper]
  );
  const totalPages = pagination.totalPages || Math.ceil(totalUsers / pagination.limit) || 1;

  return (
    <div className="space-y-6 mx-auto pb-12">
      {/* Toast Feedback */}
      {feedbackMsg && (
        <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 text-xs font-semibold flex items-center gap-2 shadow-sm animate-fade-in">
          <CheckCircleIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <span>{feedbackMsg.text}</span>
        </div>
      )}

      {/* Header section with Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-blue-600 bg-clip-text text-transparent mb-2">
              {isClient ? "My Profile & User Details" : "Users & Team Management"}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
              {isClient
                ? "View your verified platform credentials, assigned role, and linked account settings"
                : "Manage platform administrators, client operators, access credentials, and WhatsApp account bindings"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={loadUsers}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-sm shadow-blue-500/20 text-xs sm:text-sm cursor-pointer"
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            {!isClient && (
              <button
                onClick={() => navigate("/admin/users/create")}
                className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-sm shadow-green-500/20 text-xs sm:text-sm cursor-pointer"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add New User
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      {isClient ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate" title={clientAccount?.str_company_name || `ACC${String(currentUser?.str_account_id || 1).padStart(6, "0")}`}>
              {clientAccount?.str_company_name || `ACC${String(currentUser?.str_account_id || 1).padStart(6, "0")}`}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Company / Account Name
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-blue-600 font-mono">
              {Number(clientAccount?.int_current_credit || 0).toLocaleString()}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Wallet Credits
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-green-600 font-mono">
              {(clientAccount?.str_status || currentUser?.str_status || "ACTIVE").toUpperCase()}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Account Status
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-purple-600 font-mono truncate">
              @{currentUser?.str_username || "user"}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Platform Username
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-gray-900 dark:text-white font-mono">
              {totalUsers}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Total Platform Users
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-blue-600 font-mono">
              {adminCount}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Administrators
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-green-600 font-mono">
              {clientCount}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Client Accounts
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-purple-600 font-mono">
              {activeCount}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
              Active Accounts
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search user by name, username, email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          {!isClient && (
            <div className="relative">
              <FunnelIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="pl-9 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All Account Types</option>
                <option value="admin">Administrators</option>
                <option value="client">Client Users</option>
              </select>
            </div>
          )}

          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div className="relative">
            <select
              value={pagination.limit}
              onChange={(e) => {
                setPagination((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }));
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center">
            <ArrowPathIcon className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">Loading user accounts...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-16 px-4 text-center">
            <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
              <UsersIcon className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No Users Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto text-sm">
              {search || filterType !== "all" || statusFilter !== "all"
                ? "No users match your selected search or filter."
                : "No users configured. Click below to add your first user account."}
            </p>
            {!isClient && (
              <button
                onClick={() => navigate("/admin/users/create")}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-200 text-sm cursor-pointer"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add New User
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-14">
                      #
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      User Profile
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Company / Account
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Mobile
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      2FA Auth
                    </th>
                    <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredUsers.map((row, index) => {
                    const rowNumber = (pagination.page - 1) * pagination.limit + index + 1;
                    const isAdm = isRoleAdmin(row.str_role_id);
                    const isRoot = isRootSuperAdmin(row);
                    const roleInfo = getRoleInfo(row.str_role_id);
                    const initial = (row.str_name || row.str_username || "U").charAt(0).toUpperCase();

                    return (
                      <tr
                        key={row._id || index}
                        className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-400">
                          {rowNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-white text-xs ${
                              roleInfo.name.toLowerCase().includes("super")
                                ? "bg-purple-600"
                                : isAdm
                                ? "bg-blue-600"
                                : roleInfo.name.toLowerCase().includes("reseller")
                                ? "bg-teal-600"
                                : "bg-emerald-600"
                            }`}>
                              {initial}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-white text-sm">
                                {row.str_name || row.str_username}
                              </div>
                              <div className="text-[11px] text-gray-400">
                                @{row.str_username} • {row.str_email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${roleInfo.badgeColor}`}>
                            {roleInfo.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isAdm ? (
                            <span className="text-xs text-gray-400 font-medium">Platform Admin</span>
                          ) : (
                            (() => {
                              const acc = clientAccounts.find(
                                (a) =>
                                  String(a.int_account_id) === String(row.str_account_id) ||
                                  String(a._id) === String(row.str_account_id)
                              );
                              const compName = acc?.str_company_name;
                              if (compName) {
                                return (
                                  <div className="flex flex-col">
                                    <span className="font-semibold text-gray-900 dark:text-white text-xs">
                                      {compName}
                                    </span>
                                    
                                  </div>
                                );
                              }
                              if (row.str_account_id) {
                                return (
                                  <span className="font-mono text-xs px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold">
                                    ACC{String(row.str_account_id).padStart(6, "0")}
                                  </span>
                                );
                              }
                              return <span className="text-xs text-gray-400">Unassigned</span>;
                            })()
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-gray-700 dark:text-gray-300">
                          {row.str_mobile_no || "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
                            (row.str_status || "active").toLowerCase() === "active"
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                              : (row.str_status || "").toLowerCase() === "suspended"
                              ? "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                          }`}>
                            {row.str_status || "Active"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {row.bool_two_factor_enabled ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 shadow-2xs">
                              🛡️ 2FA On
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                              2FA Off
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => {
                                setSelectedUser(row);
                                setIsViewOpen(true);
                              }}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors cursor-pointer"
                              title="View User Details"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            {!isClient && (
                              <button
                                onClick={() => handleNavigateToQueue(row)}
                                className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors cursor-pointer"
                                title="Messaging Limit & Queue Management Page"
                              >
                                <ServerStackIcon className="w-4 h-4" />
                              </button>
                            )}
                            {!isClient && (
                              <button
                                onClick={() => handleManageEmbeddedSignup(row)}
                                className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                                  row.bool_embedded_signup_enabled
                                    ? "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                                    : "text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                                }`}
                                title={`Meta Embedded Signup: ${
                                  row.bool_embedded_signup_enabled
                                    ? `Enabled (${row.int_embedded_signup_used || 0}/${row.int_embedded_signup_limit || 0})`
                                    : "Disabled (Click to Enable)"
                                }`}
                              >
                                <DevicePhoneMobileIcon className="w-4 h-4" />
                              </button>
                            )}
                            {!isClient && (
                              (!isRoot || isSuper) ? (
                                <button
                                  onClick={() => handleEditUser(row)}
                                  className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-colors cursor-pointer"
                                  title="Edit User"
                                >
                                  <PencilSquareIcon className="w-4 h-4" />
                                </button>
                              ) : (
                                <span
                                  title="Super Administrator account cannot be edited by administrators"
                                  className="p-1.5 text-gray-300 dark:text-gray-600 cursor-not-allowed inline-flex items-center"
                                >
                                  <LockClosedIcon className="w-4 h-4 opacity-40" />
                                </span>
                              )
                            )}
                            {!isClient && (
                              isRoot ? (
                                <span
                                  title="Root SuperAdmin account cannot be deleted"
                                  className="p-1.5 text-gray-300 dark:text-gray-600 cursor-not-allowed inline-flex items-center"
                                >
                                  <LockClosedIcon className="w-4 h-4 opacity-40" />
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleDeleteUser(row)}
                                  className="p-1.5 text-gray-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors cursor-pointer"
                                  title="Delete User"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Showing{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {totalUsers === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {Math.min(pagination.page * pagination.limit, totalUsers)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-900 dark:text-white">{totalUsers}</span> results
              </div>
              <div className="flex items-center space-x-1.5">
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNumber;
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNumber = i + 1;
                  } else if (pagination.page >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = pagination.page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setPagination((prev) => ({ ...prev, page: pageNumber }))}
                      className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                        pagination.page === pageNumber
                          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-sm"
                          : "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
                  disabled={pagination.page === totalPages || totalPages === 0}
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Slide-over Right Sidebar Drawer for User Profile ── */}
      {isViewOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity duration-300"
            onClick={() => setIsViewOpen(false)}
          />

          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-md bg-white dark:bg-gray-800 shadow-2xl border-l border-gray-200 dark:border-gray-700 flex flex-col justify-between">
              
              {/* Drawer Header */}
              <div className="p-5 border-b border-gray-150 dark:border-gray-700 flex items-center justify-between bg-gray-50/70 dark:bg-gray-900/50">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">
                    {isClient ? "My Profile & Account Details" : "User Profile & Account"}
                  </h3>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getRoleInfo(selectedUser.str_role_id).badgeColor}`}>
                    {getRoleInfo(selectedUser.str_role_id).name}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsViewOpen(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-200/60 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer Body (Scrollable) */}
              <div className="p-6 overflow-y-auto flex-1 space-y-6">
                {/* 1. User Identity Card */}
                <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-50/70 to-indigo-50/40 dark:from-gray-700/40 dark:to-gray-800/40 border border-blue-100/80 dark:border-gray-700 flex items-center gap-4">
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold text-white shadow-sm flex-shrink-0 ${
                      getRoleInfo(selectedUser.str_role_id).name.toLowerCase().includes("super")
                        ? "bg-purple-600"
                        : isRoleAdmin(selectedUser.str_role_id)
                        ? "bg-blue-600"
                        : getRoleInfo(selectedUser.str_role_id).name.toLowerCase().includes("reseller")
                        ? "bg-teal-600"
                        : "bg-emerald-600"
                    }`}
                  >
                    {(selectedUser.str_name || selectedUser.str_username || "U").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-base font-bold text-gray-900 dark:text-white truncate">
                      {selectedUser.str_name || selectedUser.str_username}
                    </h4>
                    <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      @{selectedUser.str_username}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                      {selectedUser.str_email}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${
                      (selectedUser.str_status || "active").toLowerCase() === "active"
                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                        : "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300"
                    }`}>
                      {selectedUser.str_status || "Active"}
                    </span>
                  </div>
                </div>

                {/* 2. Personal Information */}
                <div>
                  <h5 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Personal Information
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-700">
                      <span className="text-gray-400 text-[11px] block font-semibold uppercase">Full Name</span>
                      <span className="font-semibold text-gray-900 dark:text-white mt-1 block">
                        {selectedUser.str_name || "—"}
                      </span>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-700">
                      <span className="text-gray-400 text-[11px] block font-semibold uppercase">Email Address</span>
                      <span className="font-medium text-gray-900 dark:text-white mt-1 block truncate" title={selectedUser.str_email}>
                        {selectedUser.str_email || "—"}
                      </span>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-700">
                      <span className="text-gray-400 text-[11px] block font-semibold uppercase">Mobile Number</span>
                      <span className="font-mono text-gray-900 dark:text-white mt-1 block">
                        {selectedUser.str_mobile_no || "—"}
                      </span>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-700">
                      <span className="text-gray-400 text-[11px] block font-semibold uppercase">Platform Username</span>
                      <span className="font-mono font-semibold text-purple-600 dark:text-purple-400 mt-1 block">
                        @{selectedUser.str_username}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 2.5 Security & Two-Factor Authentication (2FA) */}
                <div>
                  <h5 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Security & Two-Factor Authentication
                  </h5>
                  <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-700/40 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                          selectedUser.bool_two_factor_enabled
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                            : "bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300"
                        }`}>
                          {selectedUser.bool_two_factor_enabled ? "🛡️ 2FA Active" : "2FA Disabled"}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        {selectedUser.bool_two_factor_enabled
                          ? "Login requires 6-digit verification code after CAPTCHA"
                          : "Simple login with credentials and CAPTCHA code"}
                      </p>
                    </div>

                    {!isClient && (
                      <button
                        type="button"
                        onClick={() => handleToggle2FA(selectedUser)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                          selectedUser.bool_two_factor_enabled
                            ? "bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300"
                            : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                        }`}
                      >
                        {selectedUser.bool_two_factor_enabled ? "Disable 2FA" : "Enable 2FA"}
                      </button>
                    )}
                  </div>
                </div>

                {/* 3. Company & Account Details */}
                <div>
                  <h5 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Company & Account Details
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-700 sm:col-span-2">
                      <span className="text-gray-400 text-[11px] block font-semibold uppercase">Company / Account Name</span>
                      <div className="flex items-center justify-between mt-1">
                        <span className="font-bold text-gray-900 dark:text-white text-sm">
                          {activeAccount?.str_company_name || (isRoleAdmin(selectedUser.str_role_id) ? "Platform Admin" : "—")}
                        </span>
                       
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-700">
                      <span className="text-gray-400 text-[11px] block font-semibold uppercase">Account Status</span>
                      <span className="capitalize font-semibold text-gray-900 dark:text-white mt-1 block">
                        {activeAccount?.str_status || selectedUser.str_status || "Active"}
                      </span>
                    </div>

                    <div className="p-3 rounded-xl bg-blue-50/70 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50">
                      <span className="text-blue-800 dark:text-blue-300 text-[11px] block font-semibold uppercase tracking-wider">
                        Wallet Credits
                      </span>
                      <span className="font-bold text-blue-600 dark:text-blue-400 text-base mt-1 block font-mono">
                        ₹ {Number(activeAccount?.int_current_credit || 0).toLocaleString("en-IN")}
                      </span>
                    </div>

                    {/* Credit Expiry Date (ONLY shown if added/exists) */}
                    {activeAccount?.dttm_credit_expire_on && (
                      <div className="p-3 rounded-xl bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-800 sm:col-span-2">
                        <span className="text-amber-800 dark:text-amber-300 text-[11px] block font-semibold uppercase tracking-wider">
                          Credit Expiry Date
                        </span>
                        <span className="font-semibold text-gray-900 dark:text-white mt-1 block text-xs">
                          {new Date(activeAccount.dttm_credit_expire_on).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                    )}

                    {/* Feature Permissions */}
                    <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-700 sm:col-span-2 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-400 text-[11px] block font-bold uppercase tracking-wider">
                          Feature Permissions
                        </span>
                        {!isClient && (
                          <span className="text-[10px] text-gray-400 italic">
                            Click any permission badge to toggle directly
                          </span>
                        )}
                      </div>
                      <div className="flex items-center flex-wrap gap-2 pt-0.5">
                        {/* Live Chat */}
                        <button
                          type="button"
                          disabled={isClient}
                          onClick={() => handleTogglePermission(selectedUser, "bool_chat_enabled", "Live Chat")}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                            !isClient ? "cursor-pointer hover:scale-105 active:scale-95 shadow-xs" : ""
                          } ${
                            selectedUser.bool_chat_enabled !== false
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400"
                              : "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-400"
                          }`}
                          title={!isClient ? "Click to toggle Live Chat (Default: Enabled)" : undefined}
                        >
                          💬 Live Chat: {selectedUser.bool_chat_enabled !== false ? "Enabled" : "Disabled"}
                        </button>

                        {/* Integrations (IndiaMART, Webhooks) */}
                        <button
                          type="button"
                          disabled={isClient}
                          onClick={() => handleTogglePermission(selectedUser, "bool_indiamart_enabled", "Integrations")}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                            !isClient ? "cursor-pointer hover:scale-105 active:scale-95 shadow-xs" : ""
                          } ${
                            Boolean(selectedUser.bool_indiamart_enabled)
                              ? "bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-400"
                              : "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-400"
                          }`}
                          title={!isClient ? "Click to toggle Integrations (Default: Disabled)" : undefined}
                        >
                          ⚡ Integrations: {Boolean(selectedUser.bool_indiamart_enabled) ? "Enabled" : "Disabled"}
                        </button>

                        {/* Image Camp */}
                        <button
                          type="button"
                          disabled={isClient}
                          onClick={() => handleTogglePermission(selectedUser, "bool_image_camp_enabled", "Image Camp")}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                            !isClient ? "cursor-pointer hover:scale-105 active:scale-95 shadow-xs" : ""
                          } ${
                            Boolean(selectedUser.bool_image_camp_enabled)
                              ? "bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-950/40 dark:text-purple-400"
                              : "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-400"
                          }`}
                          title={!isClient ? "Click to toggle Image Camp (Default: Disabled)" : undefined}
                        >
                          ✨ Image Camp: {Boolean(selectedUser.bool_image_camp_enabled) ? "Enabled" : "Disabled"}
                        </button>

                        {/* Tiny URLs */}
                        <button
                          type="button"
                          disabled={isClient}
                          onClick={() => handleTogglePermission(selectedUser, "bool_tiny_url_enabled", "Tiny URLs")}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                            !isClient ? "cursor-pointer hover:scale-105 active:scale-95 shadow-xs" : ""
                          } ${
                            Boolean(selectedUser.bool_tiny_url_enabled)
                              ? "bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-400"
                              : "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-400"
                          }`}
                          title={!isClient ? "Click to toggle Tiny URLs (Default: Disabled)" : undefined}
                        >
                          🔗 Tiny URLs: {Boolean(selectedUser.bool_tiny_url_enabled) ? "Enabled" : "Disabled"}
                        </button>

                        {/* Meta WhatsApp Embedded Signup */}
                        <span
                          onClick={() => !isClient && handleManageEmbeddedSignup(selectedUser)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                            !isClient ? "cursor-pointer hover:scale-105 active:scale-95 shadow-xs" : ""
                          } ${
                            selectedUser.bool_embedded_signup_enabled
                              ? "bg-teal-50 text-teal-700 border border-teal-200 dark:bg-teal-950/40 dark:text-teal-400"
                              : "bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-800 dark:text-gray-400"
                          }`}
                          title={!isClient ? "Click to configure Embedded Signup Quota" : undefined}
                        >
                          📱 Embedded Signup:{" "}
                          {selectedUser.bool_embedded_signup_enabled
                            ? `Enabled (${selectedUser.int_embedded_signup_used || 0}/${selectedUser.int_embedded_signup_limit || 0})`
                            : "Disabled"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 4. Country Access & Messaging Routes */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Country Access & Messaging Routes
                    </h5>
                    {activeAccount?.bool_allow_all_international && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                        All International
                      </span>
                    )}
                  </div>

                  {(() => {
                    const countryList = (activeAccount?.allowed_countries && activeAccount.allowed_countries.length > 0)
                      ? activeAccount.allowed_countries
                      : ["IN"];

                    return (
                      <div className="space-y-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {countryList.map((code) => {
                            const domestic = isDomesticRoute(code);
                            const name = getCountryName(code);
                            const flag = getCountryFlag(code);

                            return (
                              <div
                                key={code}
                                className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/60 flex items-center justify-between"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-base flex-shrink-0">{flag}</span>
                                  <div className="min-w-0 truncate">
                                    <span className="font-semibold text-gray-900 dark:text-white text-xs truncate block">
                                      {name}
                                    </span>
                                    <span className="text-[10px] text-gray-400 font-mono">
                                      {code.toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${
                                    domestic
                                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                                      : "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                                  }`}
                                >
                                  {domestic ? "Domestic" : "International"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Drawer Footer with Actions */}
              <div className="p-4 border-t border-gray-150 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setIsViewOpen(false)}
                  className="px-4 py-2 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                >
                  Close
                </button>
                <div className="flex items-center gap-2">
                  {!isRootSuperAdmin(selectedUser) && !isClient && (
                    <button
                      type="button"
                      onClick={() => handleDeleteUser(selectedUser)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                      Delete User
                    </button>
                  )}
                  {!isClient && (!isRootSuperAdmin(selectedUser) || isSuper) && (
                    <button
                      type="button"
                      onClick={() => {
                        const userToEdit = selectedUser;
                        setIsViewOpen(false);
                        handleEditUser(userToEdit);
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0f172a] hover:bg-black text-white rounded-lg text-xs font-semibold shadow-xs transition-colors cursor-pointer"
                    >
                      <PencilSquareIcon className="w-3.5 h-3.5" />
                      Edit User Details
                    </button>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
}
